import React, { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { deduplicatedMarkers } from '../../../provider/models/mapModel';
import {
  fakeDelay,
  getMarkerColors,
  linkCheckSlash,
} from '../../../provider/utility';
import {
  changedMapPosition,
  clearedData,
  selectedLocation,
  selectedOccurrence,
} from '../../../store/actions';
import { serviceTypes } from '../../../store/initData/services';
import { env } from '../../../utils/env';
import BackButton from '../../BackButton/BackButton';
import DummyComponent from '../../DummyComponent';
import LinearLoading from '../../LinearLoading';
import LoadingBar from '../../LoadingBar/LoadingBar';
import Map from '../../Map/Map';
import getMarkerIcon from '../../Map/markers';
import ProgressBar from '../../ProgressBar/ProgressBar';
import SelectOptionMap from '../../SelectOptionMap/SelectOptionMap';
import styles from '../SingleInputPage/SingleInputPage.module.scss';
import { FilterButtonSection } from './FilterButtonSection';
import { FilterMenu } from './FilterMenu';
import NoWorkshopView from './NoWorkshopView';
import { SearchButton } from './SearchButton';
import { Searcher } from './Searcher';
import { MapLegend } from './MapLegend';
import {
  convWorkshopToOption,
  sortByCyclicDate,
  sortByStartDate,
} from './helpers';
import {
  ContainerOverMap,
  LinearLoadingWrapper,
  LoadingBlur,
  StyledRouter,
  SuggestButton,
  Wrapper,
} from './styled';

export default function MapPage({ hideSuggestButton }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSearcherOpen, setIsSearcherOpen] = useState(false);

  const steps = useSelector((store) => store.data.steps);
  const logoSrc = useSelector((store) => store.data.logoSrc);
  const currentPage = useSelector((store) => store.state.page);
  const currentService = useSelector((store) => store.state.service);
  const isWorkshopService = currentService.value === serviceTypes.workshop;
  const currentFranchise = useSelector((store) => store.state.city?.franchise);
  const currentAge = useSelector((store) => store.state.age);
  const ageLinkValue = currentAge?.value || 0;
  const currentRobotType = useSelector((store) => store.state.robot?.value);
  const currentLocationId = useSelector((store) => store.state.locationId);
  const currentOccurrence = useSelector((store) => store.state.occurrence);
  const mapCenter = useSelector((store) => store.state.map.center);
  const mapZoom = useSelector((store) => store.state.map.zoom);
  const availableWorkshops = useSelector((store) => store.data.workshops);

  const mapPageTexts = useSelector((store) => store.data.mapPage);

  const refreshMarkers = () => {
    // set icons
    //visibleCopy
    deduplicatedMarkers.forEach((mk) => {
      const theSameLocation = (availableWorkshops || []).filter(
        (ws) => +ws.workshop_location_id === +mk.workshop.workshop_location_id,
      );
      const colors = getMarkerColors(theSameLocation);

      mk.googleObj.setIcon({
        url: getMarkerIcon({
          primaryColor: colors.first,
          secondaryColor: colors.second,
          isSelected: +mk.workshop.workshop_location_id === +currentLocationId,
        }),
      });
    });
  };

  const panToSelected = () => {
    const selectedMarker = deduplicatedMarkers.filter(
      (mk) => +mk.workshop.workshop_location_id === +currentLocationId,
    )[0];
    selectedMarker?.googleObj.getMap().panTo(selectedMarker.googleObj.position);
    if (selectedMarker?.googleObj.getMap().getZoom() < 14) {
      //didnt work with const map = selectedMarker.googleObj.getMap()
      selectedMarker?.googleObj.getMap().setZoom(14);
    }
  };

  const handleMarkerClick = (currMarker) => {
    dispatch(clearedData('state', 'occurrence'));
    dispatch(selectedLocation(currMarker.workshop.workshop_location_id));
    navigate(
      `${env.PUBLIC_URL}/${currentService.value}/${currentFranchise.id}` +
        (isWorkshopService ? `/${ageLinkValue}/${currentRobotType}` : ``) +
        `/${currMarker.workshop.workshop_location_id}`,
    );
  };

  const handleNext = () => {
    setLoading(true);
    fakeDelay(() => {
      navigate(linkCheckSlash(location.pathname, `formularz`));
    });
  };

  const handleSuggest = () => {
    setLoading(true);
    fakeDelay(() => {
      navigate(linkCheckSlash(location.pathname, 'zaproponuj'));
    });
  };

  const handleCenterChange = (map) => {
    const center = {
      lat: map.getCenter().lat(),
      lng: map.getCenter().lng(),
    };
    dispatch(changedMapPosition('center', center));
  };

  const handleZoomChange = (map) => {
    const zoom = map.getZoom();
    dispatch(changedMapPosition('zoom', zoom));
  };

  useEffect(refreshMarkers);

  useDeepCompareEffect(panToSelected, [
    currentLocationId,
    deduplicatedMarkers.reduce(
      (accumulator, item) => accumulator.concat([item.id]),
      [],
    ),
  ]);

  let samePlaceList = [];
  if (availableWorkshops && currentLocationId) {
    samePlaceList = availableWorkshops.filter(
      (ws) => ws.workshop_location_id === currentLocationId,
    );

    isWorkshopService
      ? sortByCyclicDate(samePlaceList)
      : sortByStartDate(samePlaceList);
  }

  let mapComponent = <LoadingBar wholePage />;
  if (availableWorkshops) {
    if (availableWorkshops.length === 0) {
      mapComponent = (
        <NoWorkshopView
          hideSuggestButton={hideSuggestButton}
          onSuggest={handleSuggest}
        />
      );
    } else {
      mapComponent = (
        <Map
          refreshMarkers={refreshMarkers}
          workshops={availableWorkshops}
          onMarkerClick={handleMarkerClick}
          onCenterChange={handleCenterChange}
          onZoomChange={handleZoomChange}
          mapCenter={mapCenter}
          mapZoom={mapZoom}
        />
      );
    }
  }

  const BottomNav = () => (
    <div className={styles.mapNavWrapper}>
      <Link to={'../'} style={{ pointerEvents: 'auto' }}>
        <BackButton />
      </Link>
      {!hideSuggestButton && availableWorkshops?.length !== 0 && (
        <SuggestButton onClick={handleSuggest} />
      )}
    </div>
  );

  return (
    <Wrapper>
      {mapComponent}
      <LinearLoadingWrapper>
        {loading && <LinearLoading />}
      </LinearLoadingWrapper>
      <ContainerOverMap>
        <FilterButtonSection
          toggleFilterMenu={() => {
            setIsFilterMenuOpen((v) => !v);
            setIsSearcherOpen(false);
          }}
        />
        <SearchButton
          toggleSearcher={() => {
            setIsSearcherOpen((v) => !v);
            setIsFilterMenuOpen(false);
          }}
        />
        <MapLegend />
        <div
          className={styles.logo}
          style={{ backgroundImage: `url('${logoSrc}')` }}
        ></div>
        <ProgressBar currentStep={currentPage.step} steps={steps} />
        {isFilterMenuOpen && (
          <FilterMenu onClose={() => setIsFilterMenuOpen(false)} />
        )}
        {isSearcherOpen && (
          <Searcher onClose={() => setIsSearcherOpen(false)} />
        )}
        <BottomNav />
        <StyledRouter>
          <DummyComponent path="/"></DummyComponent>
          <SelectOptionMap
            path=":locationId/*"
            open={true}
            onClose={() =>
              navigate(
                `${env.PUBLIC_URL}/${currentService.value}/${currentFranchise.id}` +
                  (isWorkshopService
                    ? `/${ageLinkValue}/${currentRobotType}`
                    : ``),
              )
            }
            onNext={handleNext}
            onSelect={(option) => {
              dispatch(selectedOccurrence(option.ws));
              navigate(
                `${env.PUBLIC_URL}/${currentService.value}/${currentFranchise.id}` +
                  (isWorkshopService
                    ? `/${ageLinkValue}/${currentRobotType}`
                    : ``) +
                  `/${currentLocationId}/${option.ws.id}`,
              );
            }}
            selected={convWorkshopToOption(
              currentService.value,
              currentOccurrence,
              mapPageTexts,
              samePlaceList?.findIndex(
                (item) => item.id === currentOccurrence?.id,
              ),
            )}
            options={samePlaceList.map((ws, index) =>
              convWorkshopToOption(
                currentService.value,
                ws,
                mapPageTexts,
                index,
              ),
            )}
          />
        </StyledRouter>
      </ContainerOverMap>
      {loading && <LoadingBlur />}
    </Wrapper>
  );
}
